// MainLayout.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router';

import {
  Layout,
  Menu,
  Avatar,
  Typography,
  Grid,
  theme,
  Segmented,
  Button,
  Drawer,
  Badge,
} from 'antd';
import {
  KeyOutlined,
  SunOutlined,
  MoonOutlined,
  BellOutlined,
  HomeOutlined,
  IdcardOutlined,
  LockOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  ShoppingOutlined,
  InfoCircleOutlined,
  DesktopOutlined,
  DeploymentUnitOutlined,
  ApiOutlined,
  AndroidOutlined,
  MenuOutlined, // <-- Yeni import
} from '@ant-design/icons';

import LangSelect from './LangSelect';
import { useLanguage } from '../../providers/LanguageProvider';
import { useDispatch, useSelector } from 'react-redux';
import { setLanguage, setTheme } from '../../store/themeSlice';
import { useAuth } from '../../auth/AuthProvider';
import api from '../../services/api';
import Breadcrumb from 'src/components/breadcrumb/Breadcumb';
import useLicenses from 'src/hooks/useLicenses';
import { setHasW, setResolverProductIds } from 'src/store/generalSlice';
import { RESOLVER_LICENSE_PRODUCT_IDS, RESOLVER_PRODUCTS } from 'src/pages/resolver/resolverProducts';

// Yeni bileşen importu
import MenuMobile from './MenuMobile';

const { Header, Sider, Content } = Layout;
const { useToken } = theme;
const { useBreakpoint } = Grid;

/* ------------------------- MENU YARDIMCILARI ------------------------- */

const flattenMenu = (items, parentKey = null) => {
  const out = [];
  items?.forEach((it) => {
    if (!it) return;
    if (it.type === 'divider' || it.type === 'group') return; // path taşımaz
    if (it.key) out.push({ key: it.key, path: it.path, parentKey });
    if (Array.isArray(it.children)) out.push(...flattenMenu(it.children, it.key));
  });
  return out;
};

const findItemByKey = (items, key) => {
  for (const it of items || []) {
    if (!it) continue;
    if (it.key === key) return it;
    if (it.children) {
      const f = findItemByKey(it.children, key);
      if (f) return f;
    }
  }
  return null;
};

const parentChainOf = (flat, key) => {
  const parentMap = new Map(flat.map((x) => [x.key, x.parentKey]));
  const chain = [];
  let k = key;
  while (true) {
    const p = parentMap.get(k);
    if (!p) break;
    chain.push(p);
    k = p;
  }
  return chain; // örn: ['security']
};

const deriveKeyFromPath = (pathname, items) => {
  const flat = flattenMenu(items);

  // /security yolu için varsayılan alt sayfa:
  if (pathname === '/security') return 'security/sessions';

  const exact = flat.find((i) => i.path === pathname)?.key;
  if (exact) return exact;

  const partial = flat
    .filter((i) => i.path && i.path !== '/')
    .sort((a, b) => (b.path?.length || 0) - (a.path?.length || 0))
    .find((i) => pathname.startsWith(i.path));

  return partial?.key || 'home';
};

/* -------------------------- MENÜ İÇERİĞİ -------------------------- */
const buildMenuItems = (t, hasW, resolverProductIds = []) => [
  { type: "group", label: t("GROUP_ACCOUNT") },
  { key: "home", label: t("MENU_HOME"), icon: <HomeOutlined />, path: "/" },
  { key: "personal", label: t("MENU_PROFILE"), icon: <IdcardOutlined />, path: "/profile" },
  {
    key: "security",
    label: t("MENU_SECURITY"),
    icon: <LockOutlined />,
    children: [
      {
        key: "security/sessions",
        label: t("MENU_SECURITY_SESSIONS"),
        icon: <DesktopOutlined />,
        path: "/security/sessions",
      },
      {
        key: "security/password",
        label: t("MENU_SECURITY_PASSWORD"),
        icon: <KeyOutlined />,
        path: "/security/password",
      },
    ],
  },
  { type: "divider" },
  { type: "group", label: t("GROUP_PURCHASES") },
  {
    key: "payments",
    label: t("MENU_SUBSCRIPTIONS"),
    icon: <CreditCardOutlined />,
    path: "/subscriptions",
  },
  {
    key: "subscriptions-wizard",
    label: t("MENU_SUBSCRIPTIONS_WIZARD"),
    icon: <AndroidOutlined />,
    path: "/setup-wizard",
  },
  {
    key: "invoice",
    label: t("MENU_INVOICE_PROFILES"),
    icon: <FileTextOutlined />,
    path: "/invoice-profiles",
  },
  {
    key: "my-invoices",
    label: t("MENU_MY_INVOICES"),
    icon: <FileTextOutlined />,
    path: "/my-invoices",
  },
  { type: "divider" },
  { type: "group", label: t("GROUP_COMMERCE") },
  {
    key: "products",
    disabled: true,
    label: t("MENU_PRODUCTS"),
    icon: <ShoppingOutlined />,
    path: "/products",
  },
  { type: "divider" },
  { type: "group", label: t("GROUP_SUPPORT") },
  {
    key: "support",
    label: t("MENU_SUPPORT"),
    icon: <InfoCircleOutlined />,
    path: "/support",
  },
  ...(hasW || resolverProductIds.length
    ? [
      { type: "divider" },
      { type: "group", label: t("GROUP_PRODUCT_MANAGEMENT") },
    ]
    : []),
  ...(hasW
    ? [
      {
        key: "w-management",
        label: t("MENU_W_MANAGEMENT"),
        icon: <DeploymentUnitOutlined />,
        path: "/w-management",
        external: true, // <-- BU SATIRI EKLEYİN
      },
    ]
    : []),
  ...RESOLVER_PRODUCTS.filter((p) => resolverProductIds.includes(p.licenseProductId)).map((p) => ({
    key: `resolver/${p.slug}`,
    label: t(p.menuKey, p.name),
    icon: <ApiOutlined />,
    path: `/resolver/${p.slug}`,
  })),
];

/* ---------------------------- LAYOUT ---------------------------- */

export default function MainLayout({
  children,
  selectedKey,
  onMenuSelect,
  headerExtra,
}) {
  const location = useLocation();
  const { token } = useToken();
  const { t } = useLanguage();
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const dispatch = useDispatch();
  const licenses = useLicenses();

  // Mobil cihazda mıyız kontrolü
  const isMobile = screens.xs;

  const setLang = (lng) => {
    dispatch(setLanguage(lng))
  }

  const hasW = useSelector((state: any) => state.general.hasW);
  const resolverProductIds = useSelector((state: any) => state.general.resolverProductIds);

  useEffect(() => {
    licenses.fetch((e) => {
      let ctx = e.find(x => x.LicenseProductId == 13);
      if (ctx) {
        dispatch(setHasW(true));
      }
      const resolverIds = e
        .filter(x => x.IsActive && RESOLVER_LICENSE_PRODUCT_IDS.includes(Number(x.LicenseProductId)))
        .map(x => Number(x.LicenseProductId));
      dispatch(setResolverProductIds([...new Set(resolverIds)]));
    });
  }, []);

  // Tema & dil (local state)

  const mode = useSelector((state: any) => state.theme.mode);
  const setMode = (m) => {
    dispatch(setTheme(m));
    if (typeof window !== "undefined") {
      localStorage.setItem("antd-theme", m);
    }
  }

  const lang = useSelector((state: any) => state.theme.language);

  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);

  // Menü yapılarını i18n'e bağımlı olarak üret ve düzleştir
  const menuItems = useMemo(() => buildMenuItems(t, hasW, resolverProductIds), [t, hasW, resolverProductIds]);
  const flatMenu = useMemo(() => flattenMenu(menuItems), [menuItems]);

  // Seçili ve açık anahtarlar
  const [internalKey, setInternalKey] = useState(() =>
    deriveKeyFromPath(location.pathname, menuItems)
  );
  const [openKeys, setOpenKeys] = useState(() => parentChainOf(flatMenu, internalKey));

  // URL değişince seçili ve açık menüyü senkronize et
  React.useEffect(() => {
    const key = deriveKeyFromPath(location.pathname, menuItems);
    setInternalKey(key);
    setOpenKeys(parentChainOf(flatMenu, key));
  }, [location.pathname, menuItems, flatMenu]);

  const accountFirstLetter = useSelector((state: any) => state.auth.account?.FirstName?.[0] || 'U');


  const handleSelect = ({ key }) => {
    setInternalKey(key);
    onMenuSelect && onMenuSelect(key);

    const target = findItemByKey(menuItems, key);

    // --- DEĞİŞİKLİK BAŞLANGICI ---
    if (target?.path) {
      if (target.external) {
        // Eğer 'external' bayrağı varsa, window.location.href kullan
        window.location.href = target.path;
      } else {
        // Yoksa, React Router'ı (navigate) kullan
        navigate(target.path);
      }
    }
    // --- DEĞİŞİKLİK SONU ---

    setOpenKeys(parentChainOf(flatMenu, key));
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    } finally {
      logout();
      navigate('/login');
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      {/* SIDER */}

      {!isMobile && <Sider
        theme="light"
        width={232}
        breakpoint="lg"
        collapsedWidth={isMobile ? 0 : 64} // Mobil için 0 yaptık
        style={{
          borderRight: `1px solid ${token.colorBorder}`,
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: 16,
            gap: 8,
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <img
            src="https://w.parts-soft.net/img/Logo4x.png"
            alt="logo"
            style={{ width: 64, height: 64, borderRadius: 50 }}
          />
          <Typography.Title level={4} style={{ margin: 0 }}>
            {t('MY_ACCOUNT')}
          </Typography.Title>
        </div>

        <Menu
          mode="inline"
          items={menuItems as any}
          selectedKeys={[selectedKey ?? internalKey]}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          onSelect={handleSelect}
          style={{ borderRight: 0, padding: 8 }}
        />
      </Sider>}

      {/* MAIN AREA */}
      <Layout>
        {/* HEADER VEYA MOBİL MENÜ */}
        {isMobile ? (
          <MenuMobile mode={mode} setMode={setMode} menuItems={menuItems as any} onSelect={handleSelect} lang={lang} setLang={setLang} />
        ) : (
          <Header
            style={{
              height: 72,
              background: token.colorBgLayout,
              borderBottom: `1px solid ${token.colorBorder}`,
              display: 'grid',
              gridTemplateColumns: '180px 1fr auto',
              alignItems: 'center',
              paddingInline: 24,
              position: 'sticky',
              top: 0,
              zIndex: 10,
              columnGap: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }} />
            <div /> {/* orta boş alan (arama vb. için) */}
            {/* Sağ blok */}
            <div
              style={{
                justifySelf: 'end',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <Segmented
                size="large"
                value={mode}
                onChange={(v) => setMode(v)}
                options={[
                  { label: <SunOutlined />, value: 'light' },
                  { label: <MoonOutlined />, value: 'dark' },
                ]}
                style={{ borderRadius: 999 }}
              />
              <LangSelect style={{}} value={lang} onChange={setLang} />

              {headerExtra}

              <Avatar size={36} style={{ background: '#1a73e81a', color: '#1a73e8' }}>
                {accountFirstLetter}
              </Avatar>
              <Button danger size="middle" onClick={handleLogout}>
                {t('BTN_LOGOUT')}
              </Button>
            </div>
          </Header>
        )}

        {/* CONTENT */}
        <Content
          style={{
            padding: 24,
            display: 'flex',
            justifyContent: 'center',
            background: token.colorBgLayout,
          }}
        >
          <div style={{ width: '100%', maxWidth: 1120 }}>
            <div className="page-wrap">
              <Breadcrumb />
            </div>
            <Outlet />
          </div>
        </Content>
      </Layout>

      {/* Notification Drawer */}
      {/*... Drawer kodu */}
    </Layout>
  );
}